"use client";

import Image from "next/image";
import Link from "next/link";
import { useEffect, useState } from "react";
import { useRouter } from "next/navigation";
import { useDispatch, useSelector } from "react-redux";
import { ArrowRight, Calendar, Fuel, ShoppingBag, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { RootState } from "@/redux/store";
import { clearCart, removeVehicleFromCart } from "@/redux/cartSlice";
import { formatPrice } from "@/utils/format-price";
import { formatDate } from "@/utils/format-date";
import { VeicoloData } from "@/interface/VeicoloData";
import { useCheckoutActorContext } from "@/components/providers/RouteProviders";

function VehicleMeta({ label, value }: { label: string; value: string }) {
    return (
        <div className="rounded-md border border-gray-200 bg-gray-50 px-3 py-2">
            <p className="text-[11px] font-medium uppercase text-gray-500">{label}</p>
            <p className="mt-1 text-sm font-semibold text-gray-900">{value}</p>
        </div>
    );
}

export default function CarrelloPage() {
    const router = useRouter();
    const dispatch = useDispatch();
    const vehiclesInCart = useSelector((state: RootState) => state.cart.vehicles);
    const checkoutActorContext = useCheckoutActorContext();

    const [showAuthPopup, setShowAuthPopup] = useState(false);
    const [isClient, setIsClient] = useState(false);

    useEffect(() => {
        setIsClient(true);
    }, []);

    const handleClearCart = () => {
        dispatch(clearCart());
    };

    const handleRemoveVehicle = (articolo: string) => {
        dispatch(removeVehicleFromCart(articolo));
    };

    const totalAmount = isClient
        ? vehiclesInCart.reduce((acc, vehicle) => acc + +vehicle.prezzo, 0)
        : 0;

    const handleCheckout = () => {
        if (vehiclesInCart.length === 0) {
            return;
        }

        if (checkoutActorContext.mode === "guest") {
            setShowAuthPopup(true);
            return;
        }

        router.push("/checkout");
    };

    const handleRedirectToLogin = () => {
        router.push(`/auth/accedi?callbackUrl=${encodeURIComponent("/checkout")}`);
    };

    const handleRedirectToRegister = () => {
        router.push("/checkout");
    };

    return (
        <>
            <div className="mx-auto w-full max-w-[1600px] px-4 py-6 md:px-6 md:py-10">
                <div className="flex flex-col gap-4 border-b border-gray-200 pb-6 md:flex-row md:items-end md:justify-between">
                    <div>
                        <p className="text-sm font-semibold uppercase tracking-wide text-primary">Carrello</p>
                        <h1 className="mt-2 text-2xl font-bold text-gray-900 sm:text-3xl md:text-4xl">I tuoi veicoli selezionati</h1>
                        <p className="mt-2 max-w-2xl text-sm text-gray-600 md:text-base">
                            Rivedi il contenuto del carrello prima di proseguire al checkout.
                        </p>
                    </div>

                    <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between md:justify-end">
                        <span className="text-sm font-medium text-gray-600">
                            {vehiclesInCart.length} {vehiclesInCart.length === 1 ? "veicolo" : "veicoli"}
                        </span>
                        <Button
                            type="button"
                            variant="outline"
                            onClick={handleClearCart}
                            disabled={vehiclesInCart.length === 0}
                            className="h-10 w-full gap-2 sm:w-auto"
                        >
                            <Trash2 className="h-4 w-4" />
                            Svuota carrello
                        </Button>
                    </div>
                </div>

                {vehiclesInCart.length === 0 ? (
                    <Card className="mt-8 border-gray-200 shadow-sm">
                        <CardContent className="flex flex-col items-center px-6 py-12 text-center">
                            <div className="flex h-14 w-14 items-center justify-center rounded-full bg-gray-100">
                                <ShoppingBag className="h-7 w-7 text-gray-500" />
                            </div>
                            <h2 className="mt-4 text-xl font-semibold text-gray-900">Il carrello e vuoto</h2>
                            <p className="mt-2 max-w-md text-sm text-gray-600">
                                Aggiungi un veicolo dal parco auto per poter proseguire con la prenotazione o il checkout.
                            </p>
                            <div className="mt-6">
                                <Button asChild>
                                    <Link href="/parco-veicoli">Vai al parco auto</Link>
                                </Button>
                            </div>
                        </CardContent>
                    </Card>
                ) : (
                    <div className="mt-6 grid gap-6 xl:mt-8 xl:grid-cols-[minmax(0,1fr)_360px]">
                        <div className="space-y-4">
                            {vehiclesInCart.map((vehicle: VeicoloData) => (
                                <Card key={vehicle.articolo} className="border-gray-200 shadow-sm">
                                    <CardContent className="p-4 md:p-5">
                                        <div className="flex flex-col gap-5 lg:flex-row lg:items-center lg:justify-between">
                                            <div className="flex min-w-0 flex-col gap-4 sm:flex-row">
                                                <div className="relative aspect-[16/10] min-h-44 w-full shrink-0 overflow-hidden rounded-md bg-gray-50 sm:h-28 sm:min-h-0 sm:w-40 sm:aspect-auto">
                                                    <Image
                                                        src={
                                                            vehicle.images.find((image) => image.defaultimage === "1")?.image ||
                                                            "/autoinarrivo.webp"
                                                        }
                                                        alt={`${vehicle.marca} ${vehicle.modello}`}
                                                        fill
                                                        className="object-contain"
                                                        sizes="(max-width: 640px) 100vw, (max-width: 768px) 128px, 160px"
                                                    />
                                                </div>

                                                <div className="min-w-0">
                                                    <p className="text-sm font-medium text-gray-500">{vehicle.marca}</p>
                                                    <h2 className="text-lg font-bold leading-tight text-gray-900 sm:text-xl">{vehicle.modello}</h2>
                                                    <p className="mt-2 text-sm text-gray-600">Codice articolo {vehicle.articolo2}</p>
                                                </div>
                                            </div>

                                            <div className="flex flex-col gap-4 lg:items-end">
                                                <div className="grid grid-cols-2 gap-3 lg:w-[420px]">
                                                    <VehicleMeta label="Immatricolazione" value={formatDate(new Date(vehicle.immatricolazione)) || "-"} />
                                                    <VehicleMeta label="Km" value={`${vehicle.km.toLocaleString("it-IT")} km`} />
                                                    <VehicleMeta label="Alimentazione" value={vehicle.alimentazione || "-"} />
                                                    <VehicleMeta label="Cambio" value={vehicle.cambio || "-"} />
                                                </div>

                                                <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between lg:w-[420px]">
                                                    <div className="min-w-0">
                                                        <p className="text-sm text-gray-500">Prezzo</p>
                                                        <p className="text-xl font-bold text-gray-900 sm:text-2xl">{formatPrice(+vehicle.prezzo)}</p>
                                                    </div>

                                                    <Button
                                                        type="button"
                                                        variant="outline"
                                                        onClick={() => handleRemoveVehicle(vehicle.articolo)}
                                                        className="h-10 w-full gap-2 sm:w-auto"
                                                    >
                                                        <Trash2 className="h-4 w-4" />
                                                        Rimuovi
                                                    </Button>
                                                </div>
                                            </div>
                                        </div>
                                    </CardContent>
                                </Card>
                            ))}
                        </div>

                        <div className="xl:sticky xl:top-28 xl:self-start">
                            <Card className="border-gray-200 shadow-sm">
                                <CardContent className="p-5">
                                    <h2 className="text-lg font-bold text-gray-900">Riepilogo</h2>
                                    <div className="mt-5 space-y-3 border-t border-gray-200 pt-4">
                                        <div className="flex items-center justify-between text-sm text-gray-600">
                                            <span>Veicoli nel carrello</span>
                                            <span className="font-semibold text-gray-900">{vehiclesInCart.length}</span>
                                        </div>
                                        <div className="flex items-center justify-between text-base text-gray-900">
                                            <span className="font-medium">Totale</span>
                                            <span className="text-2xl font-bold">{formatPrice(totalAmount)}</span>
                                        </div>
                                    </div>

                                    <div className="mt-5 space-y-3">
                                        <Button onClick={handleCheckout} className="h-11 w-full gap-2 text-white">
                                            Continua al checkout
                                            <ArrowRight className="h-4 w-4" />
                                        </Button>
                                        <Button asChild variant="outline" className="w-full">
                                            <Link href="/parco-veicoli">Continua a cercare</Link>
                                        </Button>
                                    </div>
                                </CardContent>
                            </Card>
                        </div>
                    </div>
                )}
            </div>

            <Dialog open={showAuthPopup} onOpenChange={setShowAuthPopup}>
                <DialogContent className="max-w-md rounded-lg shadow-lg">
                    <DialogHeader>
                        <DialogTitle className="text-2xl font-semibold text-gray-800">
                            Accedi o Registrati
                        </DialogTitle>
                        <DialogDescription className="text-sm text-gray-600">
                            Per procedere con il checkout, accedi con il tuo account o registrati.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="mt-6 grid grid-cols-2 gap-x-2">
                        <Button
                            onClick={handleRedirectToLogin}
                            variant="outline"
                            className="w-full border-gray-300 text-gray-700 hover:bg-gray-100"
                        >
                            Accedi
                        </Button>
                        <Button onClick={handleRedirectToRegister} className="w-full text-white">
                            Registrati al checkout
                        </Button>
                    </div>
                </DialogContent>
            </Dialog>
        </>
    );
}
